import type { KeyboardActionId } from "~/keyboard/actionCatalog";
import type { ShortcutHandler } from "~/keyboard/KeyboardShortcutsContext";
import type { NavigationChord } from "~/keyboard/navigationChords";
import type { KeyboardScope } from "~/keyboard/scope";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { tinykeys } from "tinykeys";

import ChordPendingIndicator from "~/components/keyboard/ChordPendingIndicator";
import GridCoachmark from "~/components/keyboard/GridCoachmark";
import HintBadgeMode from "~/components/keyboard/HintBadgeMode";
import KeyboardShortcutsOverlay from "~/components/keyboard/KeyboardShortcutsOverlay";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import { useAppDispatch } from "~/hooks/reduxHooks";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useKeybindingOverrides from "~/hooks/useKeybindingOverrides";
import keyboardActions, {
	buildChordBindings,
	buildTinykeysBindingMap,
	getKeyboardAction
} from "~/keyboard/actionCatalog";
import chordPendingStore from "~/keyboard/chordPendingStore";
import createChordController from "~/keyboard/chordSequence";
import globalSearchFocus from "~/keyboard/globalSearchFocus";
import shouldSuppressForInputs from "~/keyboard/inputSuppression";
import KeyboardShortcutsContext from "~/keyboard/KeyboardShortcutsContext";
import createKeydownIgnorePredicate from "~/keyboard/keydownIgnorePredicate";
import navigationChords from "~/keyboard/navigationChords";
import { focusActiveRibbonBand } from "~/keyboard/ribbonRoving";
import orderHandlersByScope from "~/keyboard/scope";
import useShortcut from "~/keyboard/useShortcut";
import { toggleAiPanel, togglePanel } from "~/store/layoutSlice";

interface RegisteredShortcut {
	actionId: KeyboardActionId;
	allowInInputs: boolean;
	handler: ShortcutHandler;
	scope: KeyboardScope;
}

interface KeyboardShortcutsProviderProps {
	children: React.ReactNode;
}

const NavigationChordBinding = ({ chord }: { chord: NavigationChord }) => {
	const navigate = useNavigate();
	useShortcut(chord.id, () => {
		navigate(chord.path);
		return true;
	});
	return null;
};

const DefaultShortcutBindings = () => {
	const dispatch = useAppDispatch();
	const { isEnabled: isAiChatEnabled } = useFeatureFlag(FeatureFlagName.AiChat);

	useShortcut("focusGlobalSearch", () => {
		globalSearchFocus.request();
		return true;
	});

	useShortcut("focusRibbon", () => focusActiveRibbonBand());

	useShortcut("toggleDetailPanel", () => {
		dispatch(togglePanel());
		return true;
	});

	useShortcut(
		"toggleAiPanel",
		() => {
			dispatch(toggleAiPanel());
			return true;
		},
		{ enabled: isAiChatEnabled }
	);

	return (
		<>
			{navigationChords.map((chord) => (
				<NavigationChordBinding chord={chord} key={chord.id} />
			))}
		</>
	);
};

const KeyboardShortcutsProvider = ({ children }: KeyboardShortcutsProviderProps) => {
	const registryRef = useRef<Map<symbol, RegisteredShortcut>>(new Map());
	const modalScopeCountRef = useRef(0);
	const { overrides } = useKeybindingOverrides();

	const dispatchBindingMap = useMemo(() => buildTinykeysBindingMap(keyboardActions, overrides), [overrides]);
	const chordBindings = useMemo(() => buildChordBindings(keyboardActions, overrides), [overrides]);

	const registerShortcut = useCallback((actionId: KeyboardActionId, handler: ShortcutHandler) => {
		const action = getKeyboardAction(actionId);
		if (!action) return () => {};
		const token = Symbol(actionId);
		registryRef.current.set(token, {
			actionId,
			allowInInputs: action.allowInInputs ?? false,
			handler,
			scope: action.scope
		});
		if (action.scope === "modal") modalScopeCountRef.current += 1;
		return () => {
			registryRef.current.delete(token);
			if (action.scope === "modal") modalScopeCountRef.current -= 1;
		};
	}, []);

	const getRegisteredActionIds = useCallback(() => {
		const ids = new Set<KeyboardActionId>();
		for (const entry of registryRef.current.values()) ids.add(entry.actionId);
		return [...ids];
	}, []);

	const dispatchRegistered = useCallback((actionIds: KeyboardActionId[], event: KeyboardEvent): boolean => {
		const suppressed = shouldSuppressForInputs(event);
		const candidates = [...registryRef.current.values()].filter(
			(entry) => actionIds.includes(entry.actionId) && (entry.allowInInputs || !suppressed)
		);
		const ordered = orderHandlersByScope(candidates, modalScopeCountRef.current > 0);
		for (const entry of ordered) {
			if (entry.handler(event) === true) return true;
		}
		return false;
	}, []);

	useEffect(() => {
		const controller = createChordController({
			chords: chordBindings,
			onDispatch: (actionIds, event) => dispatchRegistered(actionIds, event as KeyboardEvent),
			onPendingChange: chordPendingStore.set
		});

		const ignoreKeydown = createKeydownIgnorePredicate(controller);

		const keymap: Record<string, (event: KeyboardEvent) => void> = {};
		for (const [tinykeysBinding, actionIds] of dispatchBindingMap) {
			keymap[tinykeysBinding] = (event) => {
				if (event.repeat) return;
				if (dispatchRegistered(actionIds, event)) event.preventDefault();
			};
		}

		const unsubscribe = tinykeys(window, keymap, { ignore: ignoreKeydown });
		return () => {
			controller.reset();
			unsubscribe();
		};
	}, [dispatchRegistered, dispatchBindingMap, chordBindings]);

	const contextValue = useMemo(
		() => ({ getRegisteredActionIds, registerShortcut }),
		[getRegisteredActionIds, registerShortcut]
	);

	return (
		<KeyboardShortcutsContext.Provider value={contextValue}>
			{children}
			<DefaultShortcutBindings />
			<ChordPendingIndicator />
			<HintBadgeMode />
			<GridCoachmark />
			<KeyboardShortcutsOverlay />
		</KeyboardShortcutsContext.Provider>
	);
};

export default KeyboardShortcutsProvider;
